import React from "react";
import { Box, Typography } from "@mui/material";
import { useBeforeUnload } from "react-router-dom";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxButton from "components/UI/AcxButton";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import Icon from "@mui/material/Icon";
import theme from "Theme/AppTheme";

interface DiscardChangesDialogProps {
    isOpen: boolean;
    message: string;
    hasUnsavedChanges: boolean;
    pendingAction: MouseEvent | TouchEvent | null;
    onClose?: () => void;
    onDiscard: () => void;
    setIsDiscardModalOpen: (isOpen: boolean) => void;
    setPendingAction: (action: MouseEvent | TouchEvent | null) => void;
}

const DiscardChangesDialog: React.FC<DiscardChangesDialogProps> = ({
    isOpen,
    message,
    hasUnsavedChanges,
    pendingAction,
    onClose,
    onDiscard,
    setIsDiscardModalOpen,
    setPendingAction,
}) => {
    useBeforeUnload((event) => {
        // Prevent refresh if there are unsaved changes
        if (hasUnsavedChanges) {
            event.preventDefault();
        }
    });

    return (
        <AcxDialog
            title={
                <Icon
                    sx={{
                        color: theme.palette.error.main,
                        width: "40px",
                        height: "40px",
                        alignSelf: "center",
                    }}
                >
                    <ErrorOutlinedIcon sx={{ width: "100%", height: "100%" }} />
                </Icon>
            }
            isOpen={isOpen}
            onClose={() => {
                onClose?.();
                setIsDiscardModalOpen(false);
                setPendingAction(null); // Clear pending action
            }}
            contentWidth="450px"
            dialogContentChildren={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "20px",
                            fontWeight: 600,
                        }}
                    >
                        Leave without saving?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: theme.palette.gray.main,
                        }}
                    >
                        {message}
                    </Typography>
                </Box>
            }
        >
            <AcxButton
                color="white"
                sx={{ color: theme.palette.error.main }}
                onClick={() => {
                    onDiscard();
                    // If an action was intercepted, trigger it after discard
                    if (pendingAction) {
                        (pendingAction.target as HTMLElement)?.click();
                    }
                    setPendingAction(null); // Clear pending action
                    setIsDiscardModalOpen(false);
                    onClose?.();
                }}
            >
                Discard Configuration
            </AcxButton>
        </AcxDialog>
    );
};

export default DiscardChangesDialog;
